/* tslint:disable */
/* eslint-disable */
/**
 * Distributor API
 * The Cashlink Distributor API enables you to distribute crypto securities and digital securities. It contains all building blocks from investor registration and KYC/AML to brokerage, clearing and settlement processes. While you are in full control of the frontends served to your investors you can use the Cashlink Studio to monitor and manage the activity on your platform.   Note: You cannot access data of investors or investments if they haven\'t been created via the API with your provided API credentials. Also, it\'s not possible to access data of investors that have registered via the Cashlink Signing Module, if you are using this as well.  ### Authentication  Every call to the API must be authenticated via OAuth client credentials. During setup, your point of contact will provide you with your client ID and client secret.   ### Idempotency  For certain POST endpoints, the API uses client-supplied idempotency keys to ensure that requests can be retried safely without performing the same operation twice. Repeated calls to those endpoints with the same idempotency key will return the cached response from the first call. Keys are expected to be set as header parameters with *X-Idempotency-Key*. The recommended approach for interacting with such endpoints is as follows:   - Create a unique, random key, such as a UUIDv4.   - Store the intent to call an endpoint along with the generated key in the client database.   - Flush to disk (e.g. commit an SQL transaction).   - Perform the call to the endpoint.   This ensures that even if the response from the API to a client gets lost, or the client experiences any sort of outage between steps 3 and 4, the client can always safely retry the same action.   If you already have objects corresponding to tasks in the API in your client-side database, you can use their internal IDs as idempotency keys!   ### Monetary values   Monetary values are represented as a combination of amount, decimals & currency. For example, ```{\'amount\': \'40000000\', \'decimals\': 3, \'currency\': \'EUR\'} means \'EUR 40,000.000\'```.   ### Handling documents  The document endpoint can be used to upload documents. Files are uploaded via HTTP multipart. The endpoint will return a document ID that can be used in other requests to refer to that document.   ### Webhooks  You can also register webhooks so that we can notify your application about events with regards to investments. To use that, please provide your point of contact at Cashlink with the following data:   - URL to be used as the target for webhooks  - OAuth client credentials or bearer authentication token so that we can authenticate at your server.  Webhooks are sent every time the status of an investment changes.  **POST <customer-provided URL>**  Webhooks are retried until the called endpoint returns a 2xx.  **Request**  {  \'investment_id\': \'3fa85f64-5717-4562-b3fc-2c963f66afa6\',  \'status\': \'ACCEPTED\',  \'changed_at\': \'2020-12-11T14:59:46.716Z\'  }  ### Concepts  The Cashlink Distributor API is based on the following concepts:  - Persons (legal or natural) that are users on your platform willing to invest are called investors.  - These investors will be able to invest into different financial products issued by different entities. In the API they are called products.  - The purchase of units of such a product takes place within your application and ends with a signed investment contract.  - Now you can create an investment, which contains the investor, the units, the signed investment contract and the accepted date of the investment by the issuer.   - At some point, parts of the investments are returned to the issuer because the outstanding amount has been paid back for example. Such a transaction is called a redemption.    
 *
 * The version of the OpenAPI document: v2
 * Contact: support@cashlink.de
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */


import * as runtime from '../runtime';
import type {
  InvestmentTokenForInvestor,
  PaginatedDropdownElementList,
  PaginatedInvestmentTokenForInvestorList,
} from '../models/index';
import {
    InvestmentTokenForInvestorFromJSON,
    InvestmentTokenForInvestorToJSON,
    PaginatedDropdownElementListFromJSON,
    PaginatedDropdownElementListToJSON,
    PaginatedInvestmentTokenForInvestorListFromJSON,
    PaginatedInvestmentTokenForInvestorListToJSON,
} from '../models/index';

export interface ProductsDropdownListRequest {
    field?: ProductsDropdownListFieldEnum;
    limit?: number;
    offset?: number;
}

export interface ProductsListRequest {
    limit?: number;
    offset?: number;
}

export interface ProductsRetrieveRequest {
    id: string;
}

export interface ProductsValuesRetrieveRequest {
    field: ProductsValuesRetrieveFieldEnum;
    icontains?: string;
}

/**
 * 
 */
export class ProductsApi extends runtime.BaseAPI {

    /**
     * Get dropdown elements with IDs and names.
     * Get dropdown elements
     */
    async productsDropdownListRaw(requestParameters: ProductsDropdownListRequest, initOverrides?: RequestInit | runtime.InitOverrideFunction): Promise<runtime.ApiResponse<PaginatedDropdownElementList>> {
        const queryParameters: any = {};

        if (requestParameters['field'] != null) {
            queryParameters['field'] = requestParameters['field'];
        }

        if (requestParameters['limit'] != null) {
            queryParameters['limit'] = requestParameters['limit'];
        }

        if (requestParameters['offset'] != null) {
            queryParameters['offset'] = requestParameters['offset'];
        }

        const headerParameters: runtime.HTTPHeaders = {};

        const response = await this.request({
            path: `/api/products/dropdown/`,
            method: 'GET',
            headers: headerParameters,
            query: queryParameters,
        }, initOverrides);

        return new runtime.JSONApiResponse(response, (jsonValue) => PaginatedDropdownElementListFromJSON(jsonValue));
    }

    /**
     * Get dropdown elements with IDs and names.
     * Get dropdown elements
     */
    async productsDropdownList(requestParameters: ProductsDropdownListRequest = {}, initOverrides?: RequestInit | runtime.InitOverrideFunction): Promise<PaginatedDropdownElementList> {
        const response = await this.productsDropdownListRaw(requestParameters, initOverrides);
        return await response.value();
    }

    /**
     * Mixin to add TX retry functionality to classes inheriting from APIView, such as GenericAPIView.  By default handlers are assumed TX safe, and thus will be retryable.
     */
    async productsListRaw(requestParameters: ProductsListRequest, initOverrides?: RequestInit | runtime.InitOverrideFunction): Promise<runtime.ApiResponse<PaginatedInvestmentTokenForInvestorList>> {
        const queryParameters: any = {};

        if (requestParameters['limit'] != null) {
            queryParameters['limit'] = requestParameters['limit'];
        }

        if (requestParameters['offset'] != null) {
            queryParameters['offset'] = requestParameters['offset'];
        }

        const headerParameters: runtime.HTTPHeaders = {};

        const response = await this.request({
            path: `/api/products/`,
            method: 'GET',
            headers: headerParameters,
            query: queryParameters,
        }, initOverrides);

        return new runtime.JSONApiResponse(response, (jsonValue) => PaginatedInvestmentTokenForInvestorListFromJSON(jsonValue));
    }

    /**
     * Mixin to add TX retry functionality to classes inheriting from APIView, such as GenericAPIView.  By default handlers are assumed TX safe, and thus will be retryable.
     */
    async productsList(requestParameters: ProductsListRequest = {}, initOverrides?: RequestInit | runtime.InitOverrideFunction): Promise<PaginatedInvestmentTokenForInvestorList> {
        const response = await this.productsListRaw(requestParameters, initOverrides);
        return await response.value();
    }

    /**
     * Retrieve a Product.
     */
    async productsRetrieveRaw(requestParameters: ProductsRetrieveRequest, initOverrides?: RequestInit | runtime.InitOverrideFunction): Promise<runtime.ApiResponse<InvestmentTokenForInvestor>> {
        if (requestParameters['id'] == null) {
            throw new runtime.RequiredError(
                'id',
                'Required parameter "id" was null or undefined when calling productsRetrieve().'
            );
        }

        const queryParameters: any = {};

        const headerParameters: runtime.HTTPHeaders = {};

        const response = await this.request({
            path: `/api/products/{id}/`.replace(`{${"id"}}`, encodeURIComponent(String(requestParameters['id']))),
            method: 'GET',
            headers: headerParameters,
            query: queryParameters,
        }, initOverrides);

        return new runtime.JSONApiResponse(response, (jsonValue) => InvestmentTokenForInvestorFromJSON(jsonValue));
    }

    /**
     * Retrieve a Product.
     */
    async productsRetrieve(requestParameters: ProductsRetrieveRequest, initOverrides?: RequestInit | runtime.InitOverrideFunction): Promise<InvestmentTokenForInvestor> {
        const response = await this.productsRetrieveRaw(requestParameters, initOverrides);
        return await response.value();
    }

    /**
     * Get distinct string values for a column - no pagination, capped at 50 elements.
     * Get the names one column can have
     */
    async productsValuesRetrieveRaw(requestParameters: ProductsValuesRetrieveRequest, initOverrides?: RequestInit | runtime.InitOverrideFunction): Promise<runtime.ApiResponse<Array<string>>> {
        if (requestParameters['field'] == null) {
            throw new runtime.RequiredError(
                'field',
                'Required parameter "field" was null or undefined when calling productsValuesRetrieve().'
            );
        }

        const queryParameters: any = {};

        if (requestParameters['field'] != null) {
            queryParameters['field'] = requestParameters['field'];
        }

        if (requestParameters['icontains'] != null) {
            queryParameters['icontains'] = requestParameters['icontains'];
        }

        const headerParameters: runtime.HTTPHeaders = {};

        const response = await this.request({
            path: `/api/products/values/`,
            method: 'GET',
            headers: headerParameters,
            query: queryParameters,
        }, initOverrides);

        return new runtime.JSONApiResponse<any>(response);
    }

    /**
     * Get distinct string values for a column - no pagination, capped at 50 elements.
     * Get the names one column can have
     */
    async productsValuesRetrieve(requestParameters: ProductsValuesRetrieveRequest, initOverrides?: RequestInit | runtime.InitOverrideFunction): Promise<Array<string>> {
        const response = await this.productsValuesRetrieveRaw(requestParameters, initOverrides);
        return await response.value();
    }

}

/**
 * @export
 */
export const ProductsDropdownListFieldEnum = {
    NAME: 'name'
} as const;
export type ProductsDropdownListFieldEnum = typeof ProductsDropdownListFieldEnum[keyof typeof ProductsDropdownListFieldEnum];
/**
 * @export
 */
export const ProductsValuesRetrieveFieldEnum = {
    NAME: 'name'
} as const;
export type ProductsValuesRetrieveFieldEnum = typeof ProductsValuesRetrieveFieldEnum[keyof typeof ProductsValuesRetrieveFieldEnum];
